import { computed, onMounted, ref, watch } from 'vue'

import type {
  CachedCreativeSettings,
  CreativeType,
  UseCreativeInsight,
} from '../models/creativeInsight.types'
import {
  creativeCacheId,
  discoverCreativeTypes,
  resolveCreativeFields,
} from '../service/creativeInsight'

import {
  mapColumns,
  sortRows,
  useCustomizeStore,
  useMetricCatalog,
  useReportData,
  useReportSchedules,
  type ReportColumn,
  type ReportDataState,
  type ReportSpec,
} from '@/features/reports'

export function useCreativeInsight(defaultPostData: () => string | undefined): UseCreativeInsight {
  const schedules = useReportSchedules()
  const catalog = useMetricCatalog()
  const cache = useCustomizeStore()
  const types = ref<CreativeType[]>([])
  const selectedType = ref<CreativeType | null>(null)
  const selectedMetric = ref<ReportColumn | null>(null)
  const columns = ref<ReportColumn[]>([])
  const dimensions = ref<ReportColumn[]>([])
  const metrics = ref<ReportColumn[]>([])
  const tabs = ref<string[]>([])
  const discovered = ref(false)

  const spec = computed<ReportSpec | null>(() => {
    if (!selectedType.value || dimensions.value.length === 0 || metrics.value.length === 0)
      return null
    const postData = defaultPostData()
    return {
      report_id: 66,
      edges: selectedType.value === 'Image' ? ['image_id'] : ['video_id'],
      first: 0,
      rows: 50,
      dimensions: dimensions.value
        .map((column) => column.field)
        .filter((field) => field !== 'image_url' && field !== 'thumbnail_url'),
      metrics: metrics.value.map((column) => column.field),
      sort_field: 'cost',
      sort_order: -1,
      pagination_enabled: true,
      partition_name: 'metrics_ad',
      ...(postData ? { defaultPostData: postData } : {}),
    }
  })
  const report = useReportData(spec, { immediate: false })
  const rows = computed(() =>
    sortRows(report.rows.value, selectedMetric.value, -1, { getColumn: catalog.getColumn }),
  )
  const state = computed<ReportDataState>(() => {
    if (!discovered.value) return 'loading'
    return types.value.length === 0 ? 'empty' : report.state.value
  })
  const mapFields = (fields: readonly string[]): ReportColumn[] =>
    mapColumns(fields, { getColumn: catalog.getColumn })

  function computeColumns(): void {
    const type = selectedType.value
    if (!type) return
    const saved = cache.getCached<CachedCreativeSettings>(creativeCacheId(type), 'chartSettings')
    const next = resolveCreativeFields(type, schedules.getScheduleByReportId(66), saved)
    tabs.value = next.tabs
    columns.value = mapFields(next.available)
    dimensions.value = mapFields(next.dimensions)
    metrics.value = mapFields(next.metrics)
    const cost = columns.value.find((column) => column.field === 'cost') ?? null
    selectedMetric.value =
      saved?.sortMetric && next.available.includes(saved.sortMetric.field) ? saved.sortMetric : cost
  }

  function discoverTypes(): void {
    types.value = discoverCreativeTypes(schedules.getScheduleByReportId(13))
    const preferred: CreativeType | null = types.value.includes('Video')
      ? 'Video'
      : (types.value[0] ?? null)
    const saved = preferred
      ? cache.getCached<{ selectedType?: CreativeType }>(creativeCacheId(preferred), 'selections')
      : undefined
    selectedType.value =
      saved?.selectedType && types.value.includes(saved.selectedType)
        ? saved.selectedType
        : preferred
  }

  function persist(): void {
    const type = selectedType.value
    if (!type) return
    cache.setCached(creativeCacheId(type), 'chartSettings', {
      selectedType: type,
      dimensions: dimensions.value,
      metrics: metrics.value,
      sortMetric: selectedMetric.value,
    })
    cache.setCached(creativeCacheId(type), 'selections', { selectedType: type })
  }

  function updateSettings(value: { dimensions: ReportColumn[]; metrics: ReportColumn[] }): void {
    dimensions.value = value.dimensions
    metrics.value = value.metrics
    persist()
  }

  watch(selectedType, () => {
    computeColumns()
    persist()
  })
  watch(selectedMetric, persist)
  watch(report.state, (nextState) => {
    if (nextState !== 'empty' || !selectedType.value) return
    types.value = types.value.filter((type) => type !== selectedType.value)
    selectedType.value = types.value[0] ?? null
  })
  onMounted(() => {
    discoverTypes()
    discovered.value = true
    computeColumns()
    void report.reload()
  })

  return {
    types,
    selectedType,
    selectedMetric,
    columns,
    dimensions,
    metrics,
    tabs,
    rows,
    state,
    reload: report.reload,
    updateSettings,
  }
}
